import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {Spinner} from '../../Spinner';
import {loadFieldsCatalog} from '../../../utils/fieldsCatalog';
import {templateStateFromLayout} from '../../informes/state/informeModel';
import {StaticPanelsGrid} from '../../informes/panels/StaticPanelsGrid';
import {RenderWarningsNotice} from '../../informes/RenderWarningsNotice';

export const SNAPSHOT_NO_SNAPSHOT = 'NO_SNAPSHOT';
export const SNAPSHOT_MISSING = 'MISSING';

/**
 * Un Informe en el portal del cliente: los mismos paneles del editor en sólo lectura.
 * Un solo renderer para vivo y foto — la foto llega por el MISMO render, con `snapshot_status`
 * y la fecha de la foto, y acá se dice «Datos al dd/mm/aaaa» en vez de "actualizado recién".
 *
 * `queryFn(informeId, dateRangeOverride)` se inyecta (cliente real vs preview de agencia).
 */
export function PortalInformeRenderer({informe, dateRangeOverride, queryFn, headerRight}) {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [fieldsData, setFieldsData] = useState(null);

    useEffect(() => { loadFieldsCatalog().then(setFieldsData); }, []);

    const informeId = informe ? informe.id : null;
    const overrideKey = JSON.stringify(dateRangeOverride || null);

    useEffect(() => {
        if (!informeId || !queryFn) return;
        setLoading(true);
        setError(null);
        queryFn(informeId, JSON.parse(overrideKey))
            .then((response) => {
                if (!response || response.status !== 'OK') {
                    setError(t('portal_informes.fetch_error', 'No pudimos cargar este informe. Prueba de nuevo.'));
                    setLoading(false);
                    return;
                }
                setResult(response.data || response);
                setLoading(false);
            })
            .catch(() => { setError(t('portal_informes.fetch_error', 'No pudimos cargar este informe. Prueba de nuevo.')); setLoading(false); });
    }, [informeId, overrideKey, queryFn, t]);

    const panels = useMemo(() => (informe ? templateStateFromLayout(informe.layout).panels : []), [informe]);
    const snapshotStatus = result ? result.snapshot_status : null;
    const snapshotDate = result && result.snapshot && result.snapshot.created_on ? result.snapshot.created_on : null;
    const snapshotPending = snapshotStatus === SNAPSHOT_NO_SNAPSHOT || snapshotStatus === SNAPSHOT_MISSING;

    return (
        <section className="portal-informe" data-testid="portal-informe">
            <header className="portal-editorial-header">
                <div className="portal-editorial-header-text">
                    <span className="portal-editorial-eyebrow">{t('portal_informes.section_eyebrow', 'INFORMES')}</span>
                    <h1 className="portal-editorial-title">{informe ? informe.name : ''}</h1>
                    {informe && informe.description && <p className="portal-editorial-subtitle">{informe.description}</p>}
                    {snapshotDate && (
                        <p className="portal-informe-snapshot" data-testid="snapshot-date">
                            {/* `escapeValue: false`: i18next escapa las barras de la fecha y el
                                cliente leía «Datos al 05&#x2F;09&#x2F;2026» (QA UI-14). */}
                            {t('portal_informes.data_as_of', {date: moment(snapshotDate).format('DD/MM/YYYY'),
                                interpolation: {escapeValue: false}, defaultValue: 'Datos al {{date}}'})}
                        </p>
                    )}
                </div>
                {headerRight && !snapshotDate && <div className="portal-editorial-header-right">{headerRight}</div>}
            </header>

            {loading && <div className="portal-editorial-loading"><Spinner/></div>}
            {!loading && error && (
                <div className="portal-editorial-empty">
                    <span className="portal-editorial-eyebrow -error">{t('portal_informes.error_eyebrow', 'NO PUDIMOS CARGAR')}</span>
                    <p className="portal-editorial-empty-body">{error}</p>
                </div>
            )}
            {!loading && !error && snapshotPending && (
                <div className="portal-editorial-empty" data-testid="snapshot-pending">
                    <h2 className="portal-editorial-empty-title">{t('portal_informes.snapshot_pending_title', 'Este informe se está preparando')}</h2>
                    <p className="portal-editorial-empty-body">{t('portal_informes.snapshot_pending_body', 'Tu agencia lo publicó hace instantes. Vuelve a entrar en unos minutos.')}</p>
                </div>
            )}
            {!loading && !error && !snapshotPending && (
                <div className="inf-page -portal">
                    {/* Mismo aviso que ve la agencia, en la voz del cliente y SIN el botón a
                        Conexiones: reconectar una cuenta no es su trabajo. El detalle de qué
                        cuentas cayeron ni siquiera viaja en este payload. */}
                    <RenderWarningsNotice renderResult={result} namespace="portal_informes.warnings"/>
                    <StaticPanelsGrid panels={panels} renderResult={result} fieldsData={fieldsData}/>
                </div>
            )}
        </section>
    );
}
